import { ActionRequestStatus, ActionRequestType } from '@billing/validation';
import {
  useMantineTheme,
  useComputedColorScheme,
  UnstyledButton,
  Stack,
  Group,
  Badge,
  Text,
} from '@mantine/core';
import { ActionRequest, ActionResponse } from 'interfaces';
import { formatDateOnly } from 'utils/dateUtils';

type ChainStep = { request: ActionRequest; response?: ActionResponse };

type Chain = {
  root: ActionRequest;
  steps: ChainStep[];
  latestRequest: ActionRequest;
};

export const ChainListItem = ({
  chain,
  selected,
  onClick,
  statusColor,
  typeLabel,
  typeColor,
  testID,
}: {
  chain: Chain;
  selected: boolean;
  onClick: () => void;
  statusColor: Record<ActionRequestStatus, string>;
  typeLabel: string;
  typeColor: Record<ActionRequestType, string>;
  testID?: string;
}) => {
  const theme = useMantineTheme();
  const mode = useComputedColorScheme();

  const status = chain.latestRequest?.status ?? chain.root.status;
  const type = chain.root.type;

  const title =
    type === ActionRequestType.SUPPORTING_DOCUMENTS
      ? 'Supporting Documents'
      : 'Information';

  const startedLabel = formatDateOnly(chain.root.createdAt.toString(), {
    fallback: 'N/A',
  });

  return (
    <UnstyledButton
      onClick={onClick}
      data-testid={testID}
      style={{
        width: '100%',
        textAlign: 'left',
        borderRadius: theme.radius.md,
        padding: theme.spacing.sm,
        border: `1px solid ${
          selected
            ? theme.colors.blue[6]
            : mode === 'dark'
            ? theme.colors.dark[5]
            : theme.colors.gray[2]
        }`,
        background: selected
          ? mode === 'dark'
            ? theme.colors.dark[6]
            : theme.colors.gray[0]
          : 'transparent',
      }}
    >
      <Stack gap={6}>
        <Group gap={6} wrap="nowrap">
          <Badge size="xs" color={typeColor[type]} variant="light">
            {typeLabel}
          </Badge>
          <Badge size="xs" color={statusColor[status]} variant="light">
            {status}
          </Badge>
        </Group>

        <Text fw={700} size="sm" lineClamp={1}>
          {title}
        </Text>

        <Text size="xs" c="dimmed">
          {`${startedLabel} • ${chain.steps.length} step(s)`}
        </Text>
      </Stack>
    </UnstyledButton>
  );
};
